/**
 * Structured content blocks for funding appeal email templates. AI emits this shape (small,
 * content-only) instead of hand-written HTML — `renderEmailTemplateHtmlFromBlocks` assembles a
 * fragment for the HTML editor and outreach send path.
 */

import sanitizeHtml from "sanitize-html";
import { formatCurrencyCents } from "./format";
import { FUNDING_APPEAL_LANDING_CTA_ATTR, FUNDING_APPEAL_LANDING_CTA_VALUE } from "./fundingAppealLandingCta";
import type { FundingDonationTier } from "./fundingDonationTiers";
import {
  type FundingClubSocialLinksContext,
  fundingClubSocialLinksPlaceholderHtml,
  renderFundingClubSocialLinksHtml,
  normalizeFundingClubSocialLinksSelection,
  resolveFundingClubSocialLinks,
} from "./fundingClubSocialLinks";

export type EmailGreetingBlock = { id?: string; type: "greeting"; text: string };
export type EmailParagraphBlock = { id?: string; type: "paragraph"; text: string };
export type EmailButtonBlock = { id?: string; type: "button"; label: string; href?: string };
export type EmailDividerBlock = { id?: string; type: "divider" };
export type EmailSignoffBlock = { id?: string; type: "signoff"; lines: string[] };
/** Links to club directory (MixItOne logo), Facebook, and website — URLs from club settings. */
export type EmailSocialLinksBlock = {
  id?: string;
  type: "social_links";
  directory?: boolean;
  facebook?: boolean;
  website?: boolean;
};

/** Suggested tiers from the appeal; heading/body/button label are configured on the block. */
export type EmailDonationTiersBlock = {
  id?: string;
  type: "donation_tiers";
  heading?: string;
  body?: string;
  buttonLabel: string;
};

export type EmailBlock =
  | EmailGreetingBlock
  | EmailParagraphBlock
  | EmailButtonBlock
  | EmailDividerBlock
  | EmailSignoffBlock
  | EmailSocialLinksBlock
  | EmailDonationTiersBlock;

export const EMAIL_BLOCK_TYPES: ReadonlyArray<EmailBlock["type"]> = [
  "greeting",
  "paragraph",
  "button",
  "divider",
  "signoff",
  "social_links",
  "donation_tiers",
];

export type EmailTemplateRenderContext = {
  clubSocialLinks?: FundingClubSocialLinksContext;
  donationTiers?: FundingDonationTier[];
  currency?: string;
};

/** Default href when a button block omits one — resolved server-side via appeal landing CTA marker. */
export const DEFAULT_EMAIL_BUTTON_HREF = "{{appeal_page_url}}";

export function validateEmailBlocks(input: unknown): EmailBlock[] {
  if (!Array.isArray(input)) throw new Error("blocks must be an array");
  if (input.length === 0) throw new Error("blocks must contain at least one block");
  return input.map((raw, i) => validateBlock(raw, i));
}

function validateBlock(raw: unknown, index: number): EmailBlock {
  if (!raw || typeof raw !== "object") throw new Error(`blocks[${index}] must be an object`);
  const b = raw as Record<string, unknown>;
  const type = b.type;
  switch (type) {
    case "greeting":
      requireString(b, "text", index);
      return { type: "greeting", id: optionalString(b.id), text: String(b.text) };
    case "paragraph":
      requireString(b, "text", index);
      return { type: "paragraph", id: optionalString(b.id), text: String(b.text) };
    case "button":
      requireString(b, "label", index);
      return {
        type: "button",
        id: optionalString(b.id),
        label: String(b.label),
        href: optionalString(b.href),
      };
    case "divider":
      return { type: "divider", id: optionalString(b.id) };
    case "signoff": {
      const lines = b.lines;
      if (!Array.isArray(lines) || lines.length === 0) {
        throw new Error(`blocks[${index}] (signoff) requires a non-empty lines array`);
      }
      return { type: "signoff", id: optionalString(b.id), lines: lines.map((l) => String(l)) };
    }
    case "social_links":
      return {
        type: "social_links",
        id: optionalString(b.id),
        directory: optionalBool(b.directory, true),
        facebook: optionalBool(b.facebook, true),
        website: optionalBool(b.website, true),
      };
    case "donation_tiers": {
      requireString(b, "buttonLabel", index);
      return {
        type: "donation_tiers",
        id: optionalString(b.id),
        heading: optionalString(b.heading),
        body: optionalString(b.body),
        buttonLabel: String(b.buttonLabel),
      };
    }
    default:
      throw new Error(
        `blocks[${index}] has unknown type "${String(type)}". Allowed: ${EMAIL_BLOCK_TYPES.join(", ")}`,
      );
  }
}

function requireString(b: Record<string, unknown>, key: string, index: number): void {
  if (typeof b[key] !== "string" || !(b[key] as string).trim()) {
    throw new Error(`blocks[${index}] (${String(b.type)}) requires non-empty string "${key}"`);
  }
}

function optionalString(v: unknown): string | undefined {
  return typeof v === "string" && v.trim() ? v : undefined;
}

function optionalBool(v: unknown, defaultValue: boolean): boolean {
  if (typeof v === "boolean") return v;
  return defaultValue;
}

/* -------------------------------------------------------------------------------------------- */
/* Rendering                                                                                    */
/* -------------------------------------------------------------------------------------------- */

const TEXT_COLOR = "#1f2937";
const ACCENT = "#1d4ed8";
const FONT = "Arial, Helvetica, sans-serif";

const TEXT_STYLE = `margin:0;font-size:16px;line-height:1.55;color:${TEXT_COLOR};font-family:${FONT};`;

function escapeHtml(input: string): string {
  return input.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function renderGreetingHtml(b: EmailGreetingBlock): string {
  return `<p style="${TEXT_STYLE}margin-bottom:8px;">${escapeHtml(b.text)}</p>`;
}

const PARAGRAPH_ALLOWED_TAGS = [
  "p",
  "br",
  "a",
  "strong",
  "em",
  "b",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "blockquote",
] as const;

/** Sanitize rich-text paragraph content from the block editor. */
export function sanitizeEmailTemplateParagraphHtml(html: string): string {
  return sanitizeHtml(html ?? "", {
    allowedTags: [...PARAGRAPH_ALLOWED_TAGS],
    allowedAttributes: {
      a: ["href", "target", "rel"],
    },
    allowedSchemes: ["http", "https", "mailto"],
    transformTags: {
      a: (tagName, attribs) => ({
        tagName,
        attribs: { ...attribs, target: "_blank", rel: "noopener noreferrer" },
      }),
    },
  });
}

function renderParagraphHtml(b: EmailParagraphBlock): string {
  const inner = sanitizeEmailTemplateParagraphHtml(b.text);
  return `<div style="${TEXT_STYLE}margin:8px 0;">${inner}</div>`;
}

/** Parse AI / generate output into blocks; falls back to a single paragraph block. */
export function parseEmailBlocksFromGeneratedContent(raw: string): EmailBlock[] {
  const trimmed = raw
    .trim()
    .replace(/^```(?:json)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    const candidate = Array.isArray(parsed) ? parsed : (parsed as { blocks?: unknown })?.blocks;
    if (candidate != null) return validateEmailBlocks(candidate);
  } catch {
    // fall through
  }
  const html = trimmed
    .replace(/^```(?:html)?\s*\n?/i, "")
    .replace(/\n?```\s*$/i, "")
    .trim();
  if (!html) throw new Error("Generated content was empty");
  return [{ type: "paragraph", text: html }];
}

function renderButtonHtml(b: EmailButtonBlock): string {
  const label = escapeHtml(b.label);
  const customHref = b.href?.trim();
  if (customHref) {
    const href = escapeHtml(customHref);
    return `<p style="margin:16px 0;"><a href="${href}" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:12px 20px;background-color:${ACCENT};color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;font-family:${FONT};">${label}</a></p>`;
  }
  return `<p style="margin:16px 0;"><a ${FUNDING_APPEAL_LANDING_CTA_ATTR}="${FUNDING_APPEAL_LANDING_CTA_VALUE}" href="#" style="display:inline-block;padding:12px 20px;background-color:${ACCENT};color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;font-family:${FONT};">${label}</a></p>`;
}

function renderDividerHtml(): string {
  return `<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;" />`;
}

function renderSignoffHtml(b: EmailSignoffBlock): string {
  const html = b.lines.map((l) => escapeHtml(l)).join("<br />");
  return `<p style="${TEXT_STYLE}margin-top:16px;">${html}</p>`;
}

function renderDonationTiersHtml(
  block: EmailDonationTiersBlock,
  context: EmailTemplateRenderContext | undefined,
): string {
  const tiers = context?.donationTiers ?? [];
  if (tiers.length === 0) return "";
  const currency = (context?.currency ?? "usd").trim() || "usd";
  const heading = block.heading?.trim()
    ? `<p style="${TEXT_STYLE}margin:0 0 8px;font-weight:700;font-size:18px;">${escapeHtml(block.heading)}</p>`
    : "";
  const body = block.body?.trim()
    ? `<p style="${TEXT_STYLE}margin:0 0 12px;">${escapeHtml(block.body)}</p>`
    : "";
  const label = escapeHtml(block.buttonLabel);
  const button = `<p style="margin:8px 0 0;"><a ${FUNDING_APPEAL_LANDING_CTA_ATTR}="${FUNDING_APPEAL_LANDING_CTA_VALUE}" href="#" style="display:inline-block;padding:10px 16px;background-color:${ACCENT};color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;font-family:${FONT};font-size:14px;">${label}</a></p>`;
  const rows = tiers
    .map((tier) => {
      const amount = escapeHtml(formatCurrencyCents(tier.amount_cents, currency));
      const highlightStyle = tier.highlight
        ? `border:2px solid ${ACCENT};background-color:#eff6ff;`
        : "border:1px solid #e5e7eb;background-color:#f9fafb;";
      return `<td style="padding:6px;vertical-align:top;width:${Math.floor(100 / tiers.length)}%;">
        <div style="${highlightStyle}border-radius:8px;padding:14px 12px;text-align:center;font-family:${FONT};">
          <div style="font-size:20px;font-weight:700;color:${ACCENT};margin-bottom:4px;">${amount}</div>
          <div style="font-size:14px;font-weight:600;color:${TEXT_COLOR};margin-bottom:6px;">${escapeHtml(tier.label)}</div>
          <div style="font-size:13px;line-height:1.4;color:#4b5563;margin-bottom:10px;">${escapeHtml(tier.description)}</div>
          ${button}
        </div>
      </td>`;
    })
    .join("");
  return `<div style="margin:16px 0;">${heading}${body}<table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="border-collapse:collapse;"><tr>${rows}</tr></table></div>`;
}

function renderSocialLinksHtml(
  block: EmailSocialLinksBlock,
  context: EmailTemplateRenderContext | undefined,
): string {
  const selection = normalizeFundingClubSocialLinksSelection(block);
  const links = resolveFundingClubSocialLinks(context?.clubSocialLinks, selection);
  if (links.length > 0) {
    return renderFundingClubSocialLinksHtml(links, {
      email: true,
      directoryBaseUrl: context?.clubSocialLinks?.directoryBaseUrl,
    });
  }
  return fundingClubSocialLinksPlaceholderHtml(selection);
}

function renderBlockHtml(b: EmailBlock, context?: EmailTemplateRenderContext): string {
  switch (b.type) {
    case "greeting":
      return renderGreetingHtml(b);
    case "paragraph":
      return renderParagraphHtml(b);
    case "button":
      return renderButtonHtml(b);
    case "divider":
      return renderDividerHtml();
    case "signoff":
      return renderSignoffHtml(b);
    case "social_links":
      return renderSocialLinksHtml(b, context);
    case "donation_tiers":
      return renderDonationTiersHtml(b, context);
  }
}

/** Structured email blocks → HTML fragment for `body_html` (fragment render mode). */
export function renderEmailTemplateHtmlFromBlocks(
  blocks: EmailBlock[],
  context?: EmailTemplateRenderContext,
): string {
  return blocks.map((b) => renderBlockHtml(b, context)).join("\n");
}
